/* eslint-disable react-doctor/no-adjust-state-on-prop-change, react-doctor/no-chain-state-updates, react-doctor/no-event-handler, react-doctor/no-pass-data-to-parent, react-hooks-js/set-state-in-effect */
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { AgentClient, type AgentClientOptions } from "@wags/agent-client";
import { MAX_MESSAGE_TEXT_PART_LENGTH } from "@wags/agent-runtime/common.contract.ts";
import type { SeekerScreenContext } from "@wags/agent-contracts/kits-seeker-screen.contract.ts";
import { toast } from "@wags/ui/sonner.tsx";
import { getToolName, isToolUIPart } from "ai";
import { usePostHog } from "posthog-js/react";
import { useRef, useState, useSyncExternalStore } from "react";
import { captureChatStartFailure } from "./chat-funnel";
import { useFailedChatInputs } from "./use-failed-chat-inputs";
import { initializeKitsConversation, resumeKitsConversation } from "./session";
import {
    flushViewState,
    readSeekerScreenContext,
} from "@/features/kits-conversations";
import { clearOwedFitReveal } from "@/features/kits-conversations";
import type {
    KitsConversationSession,
    WagsMessageMetadata,
    WagsUIMessage,
    WagsWidgetInteraction,
} from "@/features/kits-conversations";
import {
    exposedChatStatus,
    flushPendingChatSends,
    unhydratedPendingSends,
} from "./pending-chat-sends";
import type IKitsChatInteractions from "./kits-chat-interactions.interface";
import { newKitsInputId } from "../wags-ui-message";
import { WagsAgentChat, type WagsAgentChatSnapshot } from "./wags-agent-chat";
import { hydrateWidgetStates } from "./widget-state";

type PendingSend = {
    createdAt: string;
    inputId: string;
    metadata?: WagsMessageMetadata;
    screen?: SeekerScreenContext;
    text: string;
};

type WagsAgentConnection = {
    chat: WagsAgentChat;
    conversationId: string;
};

const CONNECTION_QUERY_KEY = ["kits-agent-connection"] as const;
let currentConnection: WagsAgentConnection | undefined;

const EMPTY_CHAT: WagsAgentChatSnapshot = {
    activeTurnId: null,
    hydrated: false,
    historicalQuickReplyToolCallIds: new Set(),
    messages: [] as WagsUIMessage[],
    status: "ready",
    terminalFailure: null,
};
const subscribeToNothing = () => () => undefined;
const getEmptyChat = () => EMPTY_CHAT;

function toOptimisticMessage(send: PendingSend): WagsUIMessage {
    return {
        id: `event:${send.inputId}`,
        metadata: {
            canonicalInputId: send.inputId,
            createdAt: send.createdAt,
            ...send.metadata,
        },
        parts: [{ text: send.text, type: "text" }],
        role: "user",
    } satisfies WagsUIMessage;
}

function compatibilityScoreResult(part: WagsUIMessage["parts"][number]) {
    if (!isToolUIPart(part) || getToolName(part) !== "compatibility_score") {
        return null;
    }
    const listingId =
        typeof part.input === "object" &&
        part.input !== null &&
        "listing_id" in part.input &&
        typeof part.input.listing_id === "string"
            ? part.input.listing_id
            : null;
    const output =
        typeof part.output === "object" &&
        part.output !== null &&
        "status" in part.output
            ? part.output
            : null;
    if (
        part.state !== "output-available" ||
        !listingId ||
        !output ||
        (output.status !== "done" && output.status !== "fail")
    ) {
        return null;
    }
    return { listingId, status: output.status };
}

export function connectAgent(
    initialSession: KitsConversationSession,
    interactions: IKitsChatInteractions,
    transport: Pick<AgentClientOptions, "fetch" | "webSocketFactory"> = {},
): WagsAgentConnection {
    let credential = initialSession;
    const client = new AgentClient({
        baseUrl: initialSession.baseUrl,
        ...transport,
        getAccessToken: async ({ forceRefresh }) => {
            if (forceRefresh) {
                const refreshed = await resumeKitsConversation();
                if (
                    !refreshed ||
                    refreshed.baseUrl !== initialSession.baseUrl ||
                    refreshed.sessionId !== initialSession.sessionId
                ) {
                    throw new Error(
                        "Refreshed Wags agent credentials changed session routing",
                    );
                }
                credential = refreshed;
            }
            return credential.token;
        },
    });
    return {
        chat: new WagsAgentChat(
            client.connect(initialSession.sessionId),
            initialSession.actorId,
            interactions,
        ),
        conversationId: initialSession.conversationId,
    };
}

function adoptAgentSession(
    session: KitsConversationSession,
    interactions: IKitsChatInteractions,
) {
    if (currentConnection?.conversationId !== session.conversationId) {
        currentConnection = connectAgent(session, interactions);
    }
    return currentConnection;
}

type KitsChatSend = {
    text: string;
    metadata?: WagsMessageMetadata;
    inputId?: string;
};

export function useKitsChatSnapshot(enabled: boolean): WagsAgentChatSnapshot {
    const { data: connection } = useQuery<WagsAgentConnection>({
        queryKey: CONNECTION_QUERY_KEY,
        queryFn: skipToken,
    });
    const chat = enabled ? connection?.chat : undefined;
    return useSyncExternalStore(
        chat?.subscribe ?? subscribeToNothing,
        chat?.getSnapshot ?? getEmptyChat,
        getEmptyChat,
    );
}

export function useKitsChat(interactions: IKitsChatInteractions) {
    const posthog = usePostHog();
    const queryClient = useQueryClient();
    const [pendingSends, setPendingSendsState] = useState<PendingSend[]>([]);
    const pendingSendsRef = useRef<PendingSend[]>([]);
    function setPendingSends(
        update: (current: PendingSend[]) => PendingSend[],
    ) {
        const next = update(pendingSendsRef.current);
        pendingSendsRef.current = next;
        setPendingSendsState(next);
    }
    const submittingInputIdsRef = useRef(new Set<string>());
    const { failedInputIds, failedInputIdsRef, markFailed, clearFailed } =
        useFailedChatInputs();
    const creatingRef = useRef(false);
    const hydrationRetryRef = useRef(false);
    const prewarmRef = useRef<
        Promise<KitsConversationSession | undefined> | undefined
    >(undefined);

    const { data: connection } = useQuery({
        queryKey: CONNECTION_QUERY_KEY,
        queryFn: async () => {
            if (currentConnection) return currentConnection;
            const resumed = await resumeKitsConversation();
            if (resumed) return adoptAgentSession(resumed, interactions);
            return currentConnection ?? null;
        },
        staleTime: Number.POSITIVE_INFINITY,
    });

    const activeConnection = connection ?? undefined;
    const conversationId = activeConnection?.conversationId;

    function flushQueuedSends() {
        if (!activeConnection) return;
        const snapshot = activeConnection.chat.getSnapshot();
        const hydratedInputIds = new Set(
            snapshot.messages.flatMap((message) => {
                const inputId = message.metadata?.canonicalInputId;
                return inputId === undefined ? [] : [inputId];
            }),
        );
        const unsent = unhydratedPendingSends(
            pendingSendsRef.current,
            hydratedInputIds,
        );
        flushPendingChatSends({
            hydrated: snapshot.hydrated,
            pendingSends: unsent,
            submittingInputIds: submittingInputIdsRef.current,
            failedInputIds: failedInputIdsRef.current,
            submit: (send) => activeConnection.chat.send(send),
            onSubmitted: (inputId) =>
                setPendingSends((current) =>
                    current.filter((send) => send.inputId !== inputId),
                ),
            onFailed: (error, inputId) => {
                markFailed([inputId]);
                console.error(
                    "[kits-chat] failed to submit queued input",
                    error,
                );
            },
        });
    }

    const subscribeToChat = (listener: () => void) => {
        if (!activeConnection) return subscribeToNothing();
        const handleChange = () => {
            const snapshot = activeConnection.chat.getSnapshot();
            if (
                !snapshot.hydrated &&
                snapshot.status === "error" &&
                !hydrationRetryRef.current
            ) {
                markFailed(pendingSendsRef.current.map((send) => send.inputId));
            }
            flushQueuedSends();
            listener();
        };
        handleChange();
        return activeConnection.chat.subscribe(handleChange);
    };
    const chatState = useSyncExternalStore(
        subscribeToChat,
        activeConnection?.chat.getSnapshot ?? getEmptyChat,
        getEmptyChat,
    );

    const hydrated = chatState.hydrated;
    useQuery({
        queryKey: ["kits-widget-states", conversationId],
        queryFn: async () => {
            await hydrateWidgetStates(conversationId as string);
            return null;
        },
        enabled: Boolean(conversationId && hydrated),
        staleTime: Number.POSITIVE_INFINITY,
    });

    const handledScoreMessages = useRef(new Set<string>());
    const processScoreMessages = () => {
        const messages = activeConnection?.chat.getSnapshot().messages ?? [];
        for (const message of messages) {
            if (handledScoreMessages.current.has(message.id)) continue;
            for (const part of message.parts) {
                const result = compatibilityScoreResult(part);
                if (result) {
                    interactions.notifyCompatibilityScoreUpdated(
                        result.listingId,
                    );
                    clearOwedFitReveal(result.listingId);
                    if (result.status === "done") {
                        interactions.invalidateDiscoveryFeed();
                    }
                }
            }
            handledScoreMessages.current.add(message.id);
        }
    };
    useSyncExternalStore(
        (listener) => {
            if (!activeConnection) return () => {};
            processScoreMessages();
            return activeConnection.chat.subscribe(() => {
                processScoreMessages();
                listener();
            });
        },
        () => chatState.messages.length,
        () => 0,
    );

    async function sendMessage(message: KitsChatSend) {
        const send = {
            createdAt: new Date().toISOString(),
            inputId: message.inputId ?? newKitsInputId(),
            metadata: message.metadata,

            screen: readSeekerScreenContext(),
            text: message.text,
        };
        if (activeConnection) {
            if (chatState.hydrated) {
                submittingInputIdsRef.current.add(send.inputId);
                setPendingSends((previous) => [...previous, send]);
                try {
                    await activeConnection.chat.send(send);
                    setPendingSends((current) =>
                        current.filter(
                            (queued) => queued.inputId !== send.inputId,
                        ),
                    );
                } catch (error) {
                    markFailed([send.inputId]);
                    throw error;
                } finally {
                    submittingInputIdsRef.current.delete(send.inputId);
                }
                return;
            }
            setPendingSends((previous) => [...previous, send]);
            if (chatState.status === "error" && !hydrationRetryRef.current) {
                hydrationRetryRef.current = true;
                void (async () => {
                    try {
                        await activeConnection.chat.start();
                    } catch (error: unknown) {
                        markFailed(
                            pendingSendsRef.current.map(
                                (queued) => queued.inputId,
                            ),
                        );
                        captureChatStartFailure(posthog, {
                            failure_site: "hydration_retry",
                            conversation_id_present: true,
                            error,
                        });
                        toast.error(
                            "Couldn't start the chat. Please try again.",
                        );
                        console.error(
                            "[kits-chat] canonical session retry failed",
                            error,
                        );
                    } finally {
                        hydrationRetryRef.current = false;
                    }
                })();
            }
            return;
        }

        setPendingSends((previous) => [...previous, send]);
        await createConversation(
            pendingSendsRef.current.filter(
                (queued) => !failedInputIdsRef.current.has(queued.inputId),
            ),
        );
    }

    async function createConversation(queuedSends: PendingSend[]) {
        if (creatingRef.current) return;
        creatingRef.current = true;
        const prewarmed = await prewarmRef.current;
        try {
            const initialized =
                prewarmed ??
                (await initializeKitsConversation({
                    data: {
                        inputs: queuedSends.filter(
                            (queued) =>
                                queued.text.length <=
                                MAX_MESSAGE_TEXT_PART_LENGTH,
                        ),
                    },
                }));
            queryClient.setQueryData(
                CONNECTION_QUERY_KEY,
                adoptAgentSession(initialized, interactions),
            );
        } catch (error: unknown) {
            markFailed(pendingSendsRef.current.map((queued) => queued.inputId));
            captureChatStartFailure(posthog, {
                failure_site: "create_conversation",
                conversation_id_present: false,
                error,
            });
            toast.error("Couldn't start the chat. Please try again.");
            console.error("[kits-chat] failed to create conversation", error);
            throw error;
        } finally {
            creatingRef.current = false;
            prewarmRef.current = undefined;
        }
    }

    function prewarmConversation() {
        if (
            activeConnection ||
            creatingRef.current ||
            prewarmRef.current !== undefined
        )
            return;
        prewarmRef.current = (async () => {
            try {
                return await initializeKitsConversation({
                    data: { inputs: [] },
                });
            } catch (error: unknown) {
                console.error(
                    "[kits-chat] failed to prewarm conversation",
                    error,
                );
                prewarmRef.current = undefined;
                return undefined;
            }
        })();
    }

    async function retryFailedMessages() {
        if (activeConnection && !chatState.hydrated)
            hydrationRetryRef.current = true;
        const retryIds = unhydratedPending
            .filter((send) => failedInputIds.has(send.inputId))
            .map((send) => send.inputId);
        clearFailed(retryIds);
        if (!activeConnection) {
            try {
                await createConversation(pendingSends);
            } catch {
                return;
            }
        } else if (!chatState.hydrated) {
            try {
                await activeConnection.chat.start();
            } catch {
                markFailed(retryIds);
            } finally {
                hydrationRetryRef.current = false;
            }
        } else {
            flushQueuedSends();
        }
    }

    const liveMessages = chatState.messages;
    const liveInputIds = new Set(
        liveMessages.flatMap((message) => {
            const inputId = message.metadata?.canonicalInputId;
            return inputId === undefined ? [] : [inputId];
        }),
    );
    const unhydratedPending = unhydratedPendingSends(
        pendingSends,
        liveInputIds,
    );
    const displayedMessages = [
        ...liveMessages,
        ...unhydratedPending.map(toOptimisticMessage),
    ];
    const lastGuidanceRef = useRef("");
    async function sendGuidedMessage(message: KitsChatSend) {
        flushViewState();
        const isAppTrigger = Boolean(message.metadata?.trigger);
        const clean = isAppTrigger
            ? message.text
            : message.text.replace(/\u200B|\u200C|\u200D|\uFEFF/g, "");
        const note = interactions.askGuidanceNote(liveMessages);
        const changed = note !== "" && note !== lastGuidanceRef.current;
        const previous = lastGuidanceRef.current;
        if (changed) lastGuidanceRef.current = note;
        try {
            await sendMessage({
                ...message,
                text: changed ? `${clean}${note}` : clean,
            });
        } catch (error) {
            if (changed && lastGuidanceRef.current === note) {
                lastGuidanceRef.current = previous;
            }
            throw error;
        }
    }

    function sendWithoutThrowing(message: KitsChatSend) {
        void (async () => {
            try {
                await sendGuidedMessage(message);
            } catch (error: unknown) {
                console.error("[kits-chat] failed to send message", error);
            }
        })();
    }

    function sendText(text: string) {
        sendWithoutThrowing({ text });
    }

    function sendInteraction(input: WagsWidgetInteraction & { text: string }) {
        flushViewState();
        const { text, ...interaction } = input;
        void (async () => {
            try {
                await sendMessage({ metadata: { interaction }, text });
            } catch (error: unknown) {
                console.error("[kits-chat] failed to send interaction", error);
            }
        })();
    }

    return {
        failedMessageCount: unhydratedPending.filter((send) =>
            failedInputIds.has(send.inputId),
        ).length,
        retryFailedMessages,
        activeTurnId: chatState.activeTurnId,
        conversationStatus: chatState.status,
        terminalFailure: chatState.terminalFailure,
        conversationId,
        hydrated,
        historicalQuickReplyToolCallIds:
            chatState.historicalQuickReplyToolCallIds,
        messages: displayedMessages,
        prewarmConversation,
        sendInteraction,
        sendMessage: sendWithoutThrowing,
        sendText,
        status: exposedChatStatus(
            unhydratedPending.some((send) => !failedInputIds.has(send.inputId)),
            chatState.status,
        ),
    };
}
